'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  GA_REQUIRE_CONSENT,
  readStoredAnalyticsConsent,
  updateGaConsent,
  writeStoredAnalyticsConsent,
  type Ga4ConsentStatus,
} from '../../lib/analytics';

type AnalyticsConsentContextValue = {
  consent: Ga4ConsentStatus;
  requireConsent: boolean;
  grantConsent: () => void;
  denyConsent: () => void;
};

const AnalyticsConsentContext =
  createContext<AnalyticsConsentContextValue | null>(null);

export function useAnalyticsConsent(): AnalyticsConsentContextValue {
  const ctx = useContext(AnalyticsConsentContext);
  if (!ctx) {
    throw new Error('useAnalyticsConsent must be used within AnalyticsConsentProvider');
  }
  return ctx;
}

export function AnalyticsConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<Ga4ConsentStatus>(() =>
    GA_REQUIRE_CONSENT ? 'pending' : 'granted',
  );

  useEffect(() => {
    setConsent(readStoredAnalyticsConsent());
  }, []);

  const grantConsent = useCallback(() => {
    writeStoredAnalyticsConsent('granted');
    updateGaConsent(true);
    setConsent('granted');
  }, []);

  const denyConsent = useCallback(() => {
    writeStoredAnalyticsConsent('denied');
    updateGaConsent(false);
    setConsent('denied');
  }, []);

  const value = useMemo(
    () => ({
      consent,
      requireConsent: GA_REQUIRE_CONSENT,
      grantConsent,
      denyConsent,
    }),
    [consent, grantConsent, denyConsent],
  );

  return (
    <AnalyticsConsentContext.Provider value={value}>
      {children}
    </AnalyticsConsentContext.Provider>
  );
}
